import React, {forwardRef, useState, useMemo} from "react";
import {createPortal} from "react-dom";
import {
    IProtocolStepDefinition,
    IGroupStepDefinition,
    TProtocolStepDefinitionId,
} from "../../types/protocolDefinitions";
import {DragDropContext, Draggable, Droppable, DraggableProvided, DraggableStateSnapshot} from "@hello-pangea/dnd";
import {Box, BoxProps, Button, Text, TextInput} from "@mantine/core";
import {PlanningStepView} from "./PlanningStepView.tsx";
import {showAddStepModal} from "../../modals";
import {IProtocolStepKind} from "../../types/protocol";
import cx from "clsx";
import classes from "./GroupStepListView.module.css";
import sharedClasses from "../../common/steps/StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {MarkdownEditor} from "@/common/components/MarkdownEditor.tsx";
import {useNeurotuneStore} from "@/common/store/neurotune";

interface GroupStepListViewProps extends BoxProps {
    group: IGroupStepDefinition;
    allSteps: Array<IProtocolStepDefinition>;
    selectedStepId?: TProtocolStepDefinitionId;
    onStepSelect?: (step: IProtocolStepDefinition) => void;
    onStepChange: (stepId: TProtocolStepDefinitionId, updates: DeepPartial<IProtocolStepDefinition>) => void;
    onAddStep: (kind: IProtocolStepKind, name: string) => void;
    onDeleteStep: (step: IProtocolStepDefinition) => void;
    onMemberOrderChanged: (group: IGroupStepDefinition, newMemberStepIds: Array<TProtocolStepDefinitionId>) => void;
}

// Helper to render draggable content, using portal when dragging to avoid backdrop-filter offset issues
const renderDraggableContent = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    children: React.ReactNode
) => {
    const content = (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={cx({[sharedClasses.itemDragging]: snapshot.isDragging})}
        >
            {children}
        </div>
    );

    // When dragging, render through portal to escape backdrop-filter container
    if (snapshot.isDragging) {
        return createPortal(content, document.body);
    }

    return content;
};

export const GroupStepListView = forwardRef<HTMLDivElement, GroupStepListViewProps>(({
                                                                                         group,
                                                                                         allSteps,
                                                                                         selectedStepId,
                                                                                         onStepSelect,
                                                                                         onStepChange,
                                                                                         onAddStep,
                                                                                         onDeleteStep,
                                                                                         onMemberOrderChanged,
                                                                                         className,
                                                                                         ...others
                                                                                     }, ref) => {
    const [localSelectedStepId, setLocalSelectedStepId] = useState<TProtocolStepDefinitionId | undefined>(selectedStepId);
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);

    // Get member steps in order based on memberStepIds
    const memberSteps = useMemo(() => {
        return group.memberStepIds
            .map(id => allSteps.find(step => step.id === id))
            .filter((step): step is IProtocolStepDefinition => step !== undefined);
    }, [group.memberStepIds, allSteps]);

    const selectedStep = useMemo(() => {
        return memberSteps.find(step => step.id === localSelectedStepId);
    }, [memberSteps, localSelectedStepId]);

    const handleStepSelect = (step: IProtocolStepDefinition) => {
        setLocalSelectedStepId(step.id);
        onStepSelect?.(step);
    };

    const handleAddStep = () => {
        showAddStepModal((kind: IProtocolStepKind, name: string) => {
            // Don't allow adding groups within groups
            if (kind !== IProtocolStepKind.GROUP) {
                onAddStep(kind, name);
            }
        }, activeCompany);
    };

    const handleDeleteStep = () => {
        if (selectedStep) {
            onDeleteStep(selectedStep);
            setLocalSelectedStepId(undefined);
        }
    };

    const handleDragEnd = ({destination, source}: { destination: any; source: any }) => {
        if (!destination || !source) return;
        if (destination.index === source.index) return;

        const newMemberStepIds = [...group.memberStepIds];
        const [movedId] = newMemberStepIds.splice(source.index, 1);
        newMemberStepIds.splice(destination.index, 0, movedId);

        onMemberOrderChanged(group, newMemberStepIds);
    };

    return (
        <Box ref={ref} className={cx(classes.root, className)} {...others}
             onClick={() => setLocalSelectedStepId(undefined)}>
            <Box className={classes.toolbar}>
                <Button size="compact-xs" onClick={handleAddStep}>
                    Add Step
                </Button>
                <TextInput
                    fw="bold"
                    size={"xxs"}
                    value={group.name}
                    onChange={(ev) => onStepChange(group.id, {name: ev.currentTarget.value})}
                />
                <Button
                    size="compact-xs"
                    disabled={!selectedStep}
                    onClick={handleDeleteStep}
                >
                    Delete Step
                </Button>
            </Box>
            <Box className={classes.listContainer}>
                <MarkdownEditor mdDataTestId={"instructions"} miw={"40em"} editingMode="full"
                                markdown={group.instructions || " "}
                                onChange={((markdown) => onStepChange(group.id, {instructions: markdown}))}
                />
                {memberSteps.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Text c="dimmed" ta="center">
                            No steps in this group. Click "Add Step" to add steps.
                        </Text>
                    </Box>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="group-steps-list" direction="vertical">
                            {(provided) => (
                                <div className={classes.steps} {...provided.droppableProps} ref={provided.innerRef}>
                                    {memberSteps.map((step, index) => (
                                        <Draggable key={step.id} index={index} draggableId={step.id}>
                                            {(provided, snapshot) => renderDraggableContent(
                                                provided,
                                                snapshot,
                                                <Box onClick={(ev) => ev.stopPropagation()}>
                                                    <PlanningStepView
                                                        step={step}
                                                        index={index}
                                                        selected={step.id === localSelectedStepId}
                                                        onSelect={() => handleStepSelect(step)}
                                                        onChange={(updates) => onStepChange(step.id, updates)}
                                                        dragHandleProps={provided.dragHandleProps}
                                                    />
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </Box>
        </Box>
    );
});